import React from "react";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import CustomDrawer from "src/components/Drawer";
import MultiLanguageInput from "src/components/Form/MultiLanguageInput";
import CustomToggle from "src/components/Toggle";
import { CurrencyDto } from "src/store/system/type";

/**
 * Paket formu — SIFIRDAN, veriye özel bileşen (jenerik form motoru DEĞİL).
 * Fiyat kartları (canlı indirim), limit stepper'ları, modül ızgarası.
 * Aynı Formik state + saga + validation; backend/işlevsellik korunur.
 */

const CUR_SYM: Record<string, string> = {
  TRY: "₺", TL: "₺", USD: "$", EUR: "€", GBP: "£",
};

const disc = (list: any, camp: any) => {
  const L = parseFloat(list) || 0;
  const C = parseFloat(camp) || 0;
  return L > 0 && C < L ? Math.round((1 - C / L) * 100) : 0;
};

// ---- küçük alt bileşenler ----
const Stepper = ({
  value, onChange, unit, step = 1,
}: { value: any; onChange: (v: number) => void; unit?: string; step?: number }) => {
  const num = value === "" || value == null ? 0 : parseInt(String(value)) || 0;
  return (
    <div className="pkg-step">
      <button type="button" onClick={() => onChange(Math.max(0, num - step))}>−</button>
      <input
        value={value === "" || value == null ? "" : num}
        onChange={(e) => {
          const raw = e.target.value.replace(/[^\d]/g, "");
          onChange(raw === "" ? (0 as any) : parseInt(raw));
        }}
      />
      {unit && <span className="pkg-step__unit">{unit}</span>}
    </div>
  );
};

interface Props {
  open: boolean;
  onHide: () => void;
  title: string;
  submitLabel?: string;
  initialValues: any;
  validationSchema: any;
  onSubmit: (values: any) => void;
  currencies: CurrencyDto[];
}

const PackageDrawer = ({
  open, onHide, title, submitLabel, initialValues, validationSchema, onSubmit, currencies,
}: Props) => {
  const { t } = useTranslation();

  const LIMITS = [
    { f: "AssetLimit", i: "mdi-car-multiple", l: t("Package.AssetLimit") },
    { f: "UserLimit", i: "mdi-account-multiple-outline", l: t("Package.UserLimit") },
    { f: "MailboxLimit", i: "mdi-email-outline", l: t("Package.MailboxLimit") },
    { f: "StorageLimit", i: "mdi-database-outline", l: t("Package.StorageLimit"), unit: "GB" },
    { f: "Location", i: "mdi-map-marker-outline", l: t("Package.Location") },
    { f: "AssetFeatureCount", i: "mdi-star-outline", l: t("Package.AssetFeatureCount") },
  ];
  const MOD_GROUPS = [
    {
      title: t("Package.SectionModules"),
      items: [
        { f: "Management", i: "mdi-tune-variant", l: t("Package.Management") },
        { f: "Maintenance", i: "mdi-wrench-outline", l: t("Package.Maintenance") },
        { f: "FinancialAccounts", i: "mdi-bank-outline", l: t("Package.FinancialAccounts") },
        { f: "ExtraServices", i: "mdi-plus-box-multiple-outline", l: t("Package.ExtraServices") },
        { f: "Expenses", i: "mdi-cash-multiple", l: t("Package.Expenses") },
        { f: "Insurances", i: "mdi-shield-check-outline", l: t("Package.Insurances") },
        { f: "Suppliers", i: "mdi-truck-outline", l: t("Package.Suppliers") },
      ],
    },
    {
      title: t("Package.GroupPlatforms"),
      items: [
        { f: "MobileApp", i: "mdi-cellphone", l: t("Package.MobileApp") },
        { f: "WebSite", i: "mdi-web", l: t("Package.WebSite") },
        { f: "APIIO", i: "mdi-code-tags", l: t("Package.APIIO") },
      ],
    },
    {
      title: t("Package.GroupIntegrations"),
      items: [
        { f: "Booqapp", i: "mdi-calendar-check-outline", l: t("Package.Booqapp") },
        { f: "UygunaKirala", i: "mdi-storefront-outline", l: t("Package.UygunaKirala") },
      ],
    },
  ];

  return (
    <CustomDrawer isOpen={open} onHide={onHide} title={title}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ values, errors, touched, setFieldValue, handleSubmit }: any) => {
          const selCur = currencies.find(
            (c) => String((c as any).EnumId) === String(values.CurrencyId)
          );
          const sym = selCur ? CUR_SYM[(selCur as any).Name] || (selCur as any).Name : "₺";
          const err = (f: string) => (touched[f] && errors[f] ? String(errors[f]) : "");
          const modCount = MOD_GROUPS.reduce(
            (a, g) => a + g.items.filter((m) => !!values[m.f]).length,
            0
          );

          return (
            <div className="m-form pkg">
              <div className="m-form__body">
                {/* KİMLİK */}
                <MultiLanguageInput
                  inputKeys={[{ field: "Name", label: t("Package.Name").toString() } as any]}
                />

                {/* Meta */}
                <div className="pkg-meta">
                  <button
                    type="button"
                    className={"pkg-metabox pkg-metabox--tg" + (values.ShowWeb ? " on" : "")}
                    onClick={() => setFieldValue("ShowWeb", !values.ShowWeb)}
                  >
                    <span>
                      <span className="t">{t("Package.ShowWeb")}</span>
                      <span className="s">{t("Package.ShowWebDesc")}</span>
                    </span>
                    <span className="pkg-sw" />
                  </button>
                  <button
                    type="button"
                    className={"pkg-metabox pkg-metabox--tg" + (values.Freemium ? " on" : "")}
                    onClick={() => setFieldValue("Freemium", !values.Freemium)}
                  >
                    <span>
                      <span className="t">{t("Package.Freemium")}</span>
                      <span className="s">{t("Package.FreemiumDesc")}</span>
                    </span>
                    <span className="pkg-sw" />
                  </button>
                  <div className="pkg-metabox">
                    <span className="t">{t("Package.Order")}</span>
                    <Stepper
                      value={values.Order}
                      onChange={(v) => setFieldValue("Order", v)}
                    />
                  </div>
                </div>

                {/* FİYATLANDIRMA */}
                <div className="m-form-section">
                  <span className="m-form-section__title">{t("Package.SectionPricing")}</span>
                </div>
                <div className="pkg-row2">
                  <div className="m-field">
                    <label className="m-field__label">{t("Package.Currency")}</label>
                    <select
                      className={"m-input" + (err("CurrencyId") ? " is-invalid" : "")}
                      value={values.CurrencyId ?? ""}
                      onChange={(e) =>
                        setFieldValue(
                          "CurrencyId",
                          e.target.value === "" ? "" : Number(e.target.value)
                        )
                      }
                    >
                      <option value="">—</option>
                      {currencies.map((c: any) => (
                        <option key={c.EnumId} value={c.EnumId}>
                          {c.Name}
                        </option>
                      ))}
                    </select>
                    {err("CurrencyId") && <div className="m-field__error">{err("CurrencyId")}</div>}
                  </div>
                  <div className="m-field">
                    <label className="m-field__label">{t("Package.CampaignEndDate")}</label>
                    <input
                      type="datetime-local"
                      className={"m-input" + (err("CampaignEndDate") ? " is-invalid" : "")}
                      value={values.CampaignEndDate || ""}
                      onChange={(e) => setFieldValue("CampaignEndDate", e.target.value)}
                    />
                    {err("CampaignEndDate") && (
                      <div className="m-field__error">{err("CampaignEndDate")}</div>
                    )}
                  </div>
                </div>

                <div className="pkg-prices">
                  {[
                    { key: "m", n: t("Package.PriceMonthly"), list: "ListPriceMonthly", camp: "CampaignPriceMonthly" },
                    { key: "y", n: t("Package.PriceYearly"), list: "ListPriceYearly", camp: "CampaignPriceYearly" },
                  ].map((p) => {
                    const d = disc(values[p.list], values[p.camp]);
                    return (
                      <div className="pkg-pcard" key={p.key}>
                        <div className="pkg-pcard__top">
                          <span className="n">{p.n}</span>
                          <span className={"pkg-disc" + (d === 0 ? " zero" : "")}>−{d}%</span>
                        </div>
                        <div className="pkg-money">
                          <span className="cur">{sym}</span>
                          <input
                            className={"m-input" + (err(p.camp) ? " is-invalid" : "")}
                            style={{ paddingLeft: 34, fontWeight: 700 }}
                            value={values[p.camp] ?? ""}
                            onChange={(e) =>
                              setFieldValue(p.camp, e.target.value.replace(/[^\d.]/g, ""))
                            }
                            placeholder="0"
                          />
                        </div>
                        <div className="pkg-listline">
                          <span className="lbl">{t("Package.ListPrice")}</span>
                          <span className="pkg-money" style={{ flex: 1 }}>
                            <span className="cur" style={{ fontSize: 12 }}>{sym}</span>
                            <input
                              className={"m-input" + (err(p.list) ? " is-invalid" : "")}
                              style={{ padding: "7px 8px 7px 26px", fontSize: 13, minHeight: 38 }}
                              value={values[p.list] ?? ""}
                              onChange={(e) =>
                                setFieldValue(p.list, e.target.value.replace(/[^\d.]/g, ""))
                              }
                              placeholder="0"
                            />
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* LİMİTLER */}
                <div className="m-form-section">
                  <span className="m-form-section__title">{t("Package.SectionLimits")}</span>
                </div>
                <div className="pkg-limits">
                  {LIMITS.map((lim) => (
                    <div className="pkg-lim" key={lim.f}>
                      <div className="pkg-lim__h">
                        <span className="pkg-lim__i">
                          <i className={"mdi " + lim.i} />
                        </span>
                        <span className="pkg-lim__l">{lim.l}</span>
                      </div>
                      <Stepper
                        value={values[lim.f]}
                        unit={(lim as any).unit}
                        onChange={(v) => setFieldValue(lim.f, v)}
                      />
                      {err(lim.f) && <div className="m-field__error">{err(lim.f)}</div>}
                    </div>
                  ))}
                </div>

                {/* MODÜLLER */}
                <div className="m-form-section">
                  <span className="m-form-section__title">
                    {t("Package.SectionModules")} · {modCount}
                  </span>
                </div>
                {MOD_GROUPS.map((g, gi) => (
                  <div key={gi} style={{ marginBottom: 14 }}>
                    {gi > 0 && <div className="pkg-subh">{g.title}</div>}
                    <div className="pkg-mods">
                      {g.items.map((m) => (
                        <button
                          type="button"
                          key={m.f}
                          className={"pkg-mod" + (values[m.f] ? " on" : "")}
                          onClick={() => setFieldValue(m.f, !values[m.f])}
                        >
                          <span className="pkg-mod__i">
                            <i className={"mdi " + m.i} />
                          </span>
                          <span className="pkg-mod__t">{m.l}</span>
                          <span className="pkg-sw" />
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="m-form__footer">
                <span className="me-auto" style={{ fontSize: 12, color: "var(--m-muted)" }}>
                  <b>{modCount}</b> {t("Package.ActiveModules")}
                </span>
                <button type="button" className="btn btn-light m-form__btn" onClick={() => onHide()}>
                  {t("FormComponent.Close")}
                </button>
                <button
                  type="button"
                  className="btn btn-primary m-form__btn"
                  onClick={() => handleSubmit()}
                >
                  <i className="mdi mdi-check me-1" />
                  {submitLabel || t("FormComponent.Save")}
                </button>
              </div>
            </div>
          );
        }}
      </Formik>
    </CustomDrawer>
  );
};

export default PackageDrawer;
